import { RealtimeClient } from "./realtime-client";
import { NatsAdapter, DEFAULT_NATS_SERVER } from "./nats-adapter";
import { userSubject } from "./subjects";
import type { RealtimeMessageHandler, RealtimePayload } from "./types";

export interface RealtimeUserClientOptions {
  name?: string;
  onSubscriptionError?: (error: Error) => void;
  onInitError?: () => void;
}

export interface IRealtimeUserClient {
  init: () => Promise<void>;
  subscribe: <T extends RealtimePayload["action"]>(
    actions: T[],
    handler: RealtimeMessageHandler<Extract<RealtimePayload, { action: T }>>,
  ) => Promise<void>;
  unsubscribe: (handler: RealtimeMessageHandler<never>) => Promise<void>;
  close: () => Promise<void>;
}

export class RealtimeUserClient implements IRealtimeUserClient {
  #topic: string;
  #client: RealtimeClient;
  #ownsClient: boolean;
  #actionHandlers = new Map<RealtimeMessageHandler<never>, RealtimeMessageHandler>();

  constructor(topicOrEntityId: string, clientOrServer?: RealtimeClient | string, options?: RealtimeUserClientOptions) {
    if (clientOrServer instanceof RealtimeClient) {
      this.#client = clientOrServer;
      this.#ownsClient = false;
    } else {
      const server = clientOrServer ?? DEFAULT_NATS_SERVER;
      const adapter = new NatsAdapter({
        server,
        name: options?.name,
        onSubscriptionError: options?.onSubscriptionError,
        onInitError: options?.onInitError,
      });
      this.#client = new RealtimeClient(adapter, { onSubscriptionError: options?.onSubscriptionError });
      this.#ownsClient = true;
    }
    // Supports custom topics like "classroom.${classId}"; a bare id becomes "user.${entityId}"
    this.#topic = userSubject(topicOrEntityId);
  }

  init() {
    return this.#client.init();
  }

  async subscribe<T extends RealtimePayload["action"]>(
    actions: T[],
    handler: RealtimeMessageHandler<Extract<RealtimePayload, { action: T }>>,
  ) {
    await this.init();
    function filteredHandler(message: RealtimePayload) {
      if (actions.includes(message.action as T)) {
        return handler(message as Extract<RealtimePayload, { action: T }>);
      }
    }

    this.#actionHandlers.set(handler, filteredHandler);

    this.#client.subscribe(this.#topic, filteredHandler);
  }

  async unsubscribe(handler: RealtimeMessageHandler<never>) {
    await this.init();
    const mappedHandler = this.#actionHandlers.get(handler);

    if (!mappedHandler) {
      return;
    }

    this.#client.unsubscribe(this.#topic, mappedHandler);
    this.#actionHandlers.delete(handler);
  }

  async close() {
    if (this.#ownsClient) {
      this.#actionHandlers.clear();
      await this.#client.close();
    }
  }
}
